/**
 * Line km on contract variations (decision 258): staff enter the signed change of the contract's line km when they
 * record a variation, and the Operations Hub adds the changes the client agreed to the signed contract's km
 * (`contractKm` in ops.ts). Blank means the variation does not change the line km.
 */

export type LineKmChange =
  { readonly ok: true; readonly value: number | null } | { readonly ok: false; readonly message: string };

/** The cloud's bound (`contract_variation_line_km_change_check`). */
export const LINE_KM_CHANGE_LIMIT = 1_000_000;

/** What staff typed: blank, or a signed number of km ("−1,640", "-1640 km", "+500"). */
export function parseLineKmChange(text: string): LineKmChange {
  const t = text
    .trim()
    .replace(/−/g, '-')
    .replace(/,/g, '')
    .replace(/\s*km$/i, '')
    .trim();
  if (t === '') return { ok: true, value: null };
  if (!/^[+-]?(\d+(\.\d*)?|\.\d+)$/.test(t))
    return { ok: false, message: 'Enter the line km change as a number, for example −1,640 or 500.' };
  const value = Number(t);
  if (value === 0)
    return {
      ok: false,
      message: 'A line km change cannot be 0. Leave it blank when the line km do not change.',
    };
  if (Math.abs(value) >= LINE_KM_CHANGE_LIMIT)
    return { ok: false, message: 'A line km change must be less than 1,000,000 km.' };
  return { ok: true, value };
}

/** "−1,640 km", "+500 km" (U+2212 for minus). */
export function lineKmChangeText(km: number): string {
  const v = Math.abs(km).toLocaleString('en-AU', { maximumFractionDigits: 2 });
  return `${km < 0 ? '−' : '+'}${v} km`;
}
